/* ==========================================================================
   Trufairs Wishlist
   --------------------------------------------------------------------------
   Local-first wishlist for the Trufairs Impulse theme.

   Design rules this file follows:
   - localStorage is the source of truth. The UI updates from it synchronously.
   - The analytics API is fire-and-forget and can never block, revert or break
     a customer's wishlist action.
   - One delegated click listener on `document`, never per-button listeners.
   - Buttons render "cold" from Liquid and are hydrated here, so any markup
     that arrives later (filters, quick view, predictive search, recently
     viewed, theme editor reloads) is handled by the same code path.
   - Everything hangs off window.TrufairsWishlist. No other globals.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.TrufairsWishlistConfig || {};

  var STORAGE_KEY = 'trufairs_wishlist_v1';
  var VISITOR_KEY = 'wishlist_visitor_id';
  var MAX_ITEMS = 250;

  var SELECTORS = {
    button: '[data-wishlist-button]',
    tooltip: '[data-wishlist-tooltip]',
    loveCount: '[data-wishlist-love-count]',
    headerCount: '[data-wishlist-count]',
    headerLink: '[data-wishlist-link]'
  };

  var CLASSES = {
    active: 'wishlist-button--active',
    pop: 'wishlist-button--pop'
  };

  var STRINGS = Object.assign(
    {
      add: 'Add to wishlist',
      remove: 'Remove from wishlist',
      added: 'Item added to wishlist.',
      removed: 'Item removed from wishlist.'
    },
    CFG.strings || {}
  );

  /* ------------------------------------------------------------------
     Storage — guarded so Safari private mode / disabled storage degrades
     to an in-memory wishlist for the current page instead of throwing.
     ------------------------------------------------------------------ */

  /**
   * requestAnimationFrame with a timeout fallback. Every browser Shopify
   * supports has rAF, but some embedded webviews expose a Window without it,
   * and a missing frame scheduler must not be able to throw out of a click
   * handler and leave the wishlist half-updated.
   */
  function raf(callback) {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback);
      return;
    }
    window.setTimeout(callback, 16);
  }

  var storageAvailable = (function () {
    try {
      var probe = '__tfwl__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();

  // In-memory mirror. Every read goes through here so repeated hydration
  // passes never hammer localStorage (Core Web Vitals: no sync storage reads
  // in hot paths).
  var items = null;
  var index = null;

  function readStorage() {
    if (!storageAvailable) return [];
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeStorage() {
    if (!storageAvailable) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* Quota or private mode — the in-memory list still works this session. */
    }
  }

  /**
   * Coerce a price to an integer number of minor units (cents), or null.
   * Accepts the integer Shopify uses in product JSON and the string that
   * comes off a data-product-price attribute. Anything else is dropped
   * rather than guessed at, so GA4 never receives a bogus `value`.
   */
  function normalizePrice(value) {
    if (value == null || value === '') return null;
    var n = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (!isFinite(n) || n < 0) return null;
    return n;
  }

  function normalize(entry) {
    if (!entry || entry.id == null) return null;
    var id = String(entry.id);
    if (!id || id === 'null' || id === 'undefined') return null;
    // price / vendor / type are optional and exist only to feed GA4. They are
    // written when the source markup carries them and simply absent on
    // entries saved by an earlier build, which stays a valid wishlist entry.
    var out = {
      id: id,
      handle: entry.handle ? String(entry.handle) : '',
      variantId: entry.variantId != null && entry.variantId !== '' ? String(entry.variantId) : null,
      title: entry.title ? String(entry.title).slice(0, 255) : '',
      addedAt: typeof entry.addedAt === 'number' ? entry.addedAt : Date.now()
    };
    var price = normalizePrice(entry.price);
    if (price !== null) out.price = price;
    if (entry.vendor) out.vendor = String(entry.vendor).slice(0, 120);
    if (entry.type) out.type = String(entry.type).slice(0, 120);
    return out;
  }

  function load() {
    if (items !== null) return;
    var seen = Object.create(null);
    items = [];
    readStorage().forEach(function (raw) {
      var entry = normalize(raw);
      // Duplicate guard on read as well as on write: a list that somehow
      // gained duplicates (older build, manual edit) is healed here.
      if (!entry || seen[entry.id]) return;
      seen[entry.id] = true;
      items.push(entry);
    });
    rebuildIndex();
  }

  function rebuildIndex() {
    index = Object.create(null);
    items.forEach(function (entry) {
      index[entry.id] = entry;
    });
  }

  /* ------------------------------------------------------------------
     Anonymous visitor id — random, never derived from the browser.
     ------------------------------------------------------------------ */

  var visitorId = null;

  function randomId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        var bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        // RFC 4122 v4 layout.
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        var hex = [];
        for (var i = 0; i < bytes.length; i++) {
          hex.push((bytes[i] + 0x100).toString(16).slice(1));
        }
        return (
          hex.slice(0, 4).join('') + '-' +
          hex.slice(4, 6).join('') + '-' +
          hex.slice(6, 8).join('') + '-' +
          hex.slice(8, 10).join('') + '-' +
          hex.slice(10, 16).join('')
        );
      }
    } catch (e) {
      /* fall through */
    }
    // Last-resort fallback. Only reached on browsers without Web Crypto.
    return 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function getVisitorId() {
    if (visitorId) return visitorId;
    if (!storageAvailable) {
      visitorId = randomId();
      return visitorId;
    }
    try {
      var stored = window.localStorage.getItem(VISITOR_KEY);
      if (stored && stored.length >= 8 && stored.length <= 64) {
        visitorId = stored;
        return visitorId;
      }
      visitorId = randomId();
      window.localStorage.setItem(VISITOR_KEY, visitorId);
    } catch (e) {
      visitorId = randomId();
    }
    return visitorId;
  }

  /* ------------------------------------------------------------------
     Analytics transport — fire and forget, never awaited by the UI.
     ------------------------------------------------------------------ */

  var apiBase = (function () {
    var url = typeof CFG.apiUrl === 'string' ? CFG.apiUrl.trim().replace(/\/+$/, '') : '';
    if (!url) return '';
    // Second validation pass. Liquid already enforced https, but the setting
    // could be edited to anything, and a bad value must not throw at runtime.
    try {
      var parsed = new URL(url);
      if (parsed.protocol !== 'https:') return '';
      return parsed.origin + parsed.pathname.replace(/\/+$/, '');
    } catch (e) {
      return '';
    }
  })();

  function apiEnabled() {
    return apiBase !== '';
  }

  function sendEvent(action, entry) {
    if (!apiEnabled()) return;

    var payload = {
      action: action,
      productId: entry.id,
      productHandle: entry.handle || undefined,
      productTitle: entry.title || undefined,
      variantId: entry.variantId || undefined,
      shopDomain: CFG.shopDomain || window.location.hostname,
      visitorId: getVisitorId()
    };

    // No timestamp and no IP are sent: the server derives both.
    try {
      fetch(apiBase + '/api/wishlist', {
        method: 'POST',
        mode: 'cors',
        cache: 'no-store',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) return null;
          return res.json().catch(function () { return null; });
        })
        .then(function (data) {
          if (data && data.success && typeof data.count === 'number') {
            setLoveCount(entry.id, data.count);
          }
        })
        .catch(function (err) {
          if (window.Shopify && window.Shopify.designMode) {
            console.warn('[TrufairsWishlist] analytics request failed (local wishlist unaffected)', err);
          }
        });
    } catch (e) {
      /* Never surfaced to the customer. */
    }
  }

  /* ------------------------------------------------------------------
     Love counts — batched, cached, opt-in.
     ------------------------------------------------------------------ */

  var loveCounts = Object.create(null);
  var countQueue = Object.create(null);
  var countTimer = null;
  var COUNT_BATCH_SIZE = 50;

  function setLoveCount(productId, count) {
    loveCounts[String(productId)] = count;
    paintLoveCount(String(productId));
  }

  function paintLoveCount(productId) {
    if (!CFG.showLoveCounts) return;
    var count = loveCounts[productId];
    var minimum = typeof CFG.loveCountMinimum === 'number' ? CFG.loveCountMinimum : 1;
    buttonsFor(productId).forEach(function (btn) {
      var el = btn.querySelector(SELECTORS.loveCount);
      if (!el) return;
      if (typeof count === 'number' && count >= minimum) {
        el.textContent = String(count);
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
  }

  function queueLoveCount(productId) {
    if (!CFG.showLoveCounts || !apiEnabled()) return;
    var id = String(productId);
    if (id in loveCounts || countQueue[id]) return;
    countQueue[id] = true;
    if (countTimer) return;
    countTimer = window.setTimeout(flushLoveCounts, 120);
  }

  function flushLoveCounts() {
    countTimer = null;
    var ids = Object.keys(countQueue);
    countQueue = Object.create(null);
    if (!ids.length) return;

    // One request per 50 products, never one per card.
    for (var i = 0; i < ids.length; i += COUNT_BATCH_SIZE) {
      requestLoveCounts(ids.slice(i, i + COUNT_BATCH_SIZE));
    }
  }

  function requestLoveCounts(ids) {
    var url =
      apiBase +
      '/api/wishlist/counts?shopDomain=' +
      encodeURIComponent(CFG.shopDomain || '') +
      '&productIds=' +
      encodeURIComponent(ids.join(','));

    fetch(url, { method: 'GET', mode: 'cors', cache: 'no-store' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data.success || !data.counts) return;
        Object.keys(data.counts).forEach(function (id) {
          setLoveCount(id, data.counts[id]);
        });
      })
      .catch(function () {
        /* Counts are decorative. Silence is correct here. */
      });
  }

  /* ------------------------------------------------------------------
     Toast
     ------------------------------------------------------------------ */

  var toastRegion = null;
  var toastTimer = null;

  function getToastRegion() {
    if (toastRegion && document.body.contains(toastRegion)) return toastRegion;
    // Reuse an existing region if one is already in the DOM (e.g. after a
    // theme editor section reload) rather than stacking containers.
    toastRegion = document.getElementById('WishlistToastRegion');
    if (!toastRegion) {
      toastRegion = document.createElement('div');
      toastRegion.id = 'WishlistToastRegion';
      toastRegion.className = 'wishlist-toast-region';
      toastRegion.setAttribute('role', 'status');
      toastRegion.setAttribute('aria-live', 'polite');
      toastRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toastRegion);
    }
    return toastRegion;
  }

  function toast(message) {
    var region = getToastRegion();
    region.innerHTML = '';

    var el = document.createElement('div');
    el.className = 'wishlist-toast';
    el.textContent = message;
    region.appendChild(el);

    // Force a frame so the enter transition runs.
    raf(function () {
      el.classList.add('wishlist-toast--visible');
    });

    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      el.classList.remove('wishlist-toast--visible');
      window.setTimeout(function () {
        if (el.parentNode === region) region.removeChild(el);
      }, 300);
    }, 2600);
  }

  /* ------------------------------------------------------------------
     DOM sync
     ------------------------------------------------------------------ */

  function buttonsFor(productId) {
    return Array.prototype.slice.call(
      document.querySelectorAll(
        SELECTORS.button + '[data-product-id="' + String(productId).replace(/"/g, '') + '"]'
      )
    );
  }

  function paintButton(btn) {
    var id = btn.getAttribute('data-product-id');
    if (!id) return;
    var active = has(id);

    btn.classList.toggle(CLASSES.active, active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute('aria-label', active ? STRINGS.remove : STRINGS.add);

    var tip = btn.querySelector(SELECTORS.tooltip);
    if (tip) tip.textContent = active ? STRINGS.remove : STRINGS.add;

    queueLoveCount(id);
    paintLoveCount(id);
  }

  /**
   * Hydrate every wishlist button inside `root` (default: document).
   * Idempotent — safe to call as often as you like.
   */
  function refresh(root) {
    load();
    var scope = root && root.querySelectorAll ? root : document;
    var list = Array.prototype.slice.call(scope.querySelectorAll(SELECTORS.button));
    // If `root` is itself a button (single-node injection), include it.
    if (root && root.matches && root.matches(SELECTORS.button)) list.push(root);
    list.forEach(paintButton);
    paintHeaderCount();
  }

  function paintHeaderCount() {
    var total = count();
    document.querySelectorAll(SELECTORS.headerCount).forEach(function (el) {
      el.textContent = total > 99 ? '99+' : String(total);
      el.classList.toggle('wishlist-count--visible', total > 0);
      if (total > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
    document.querySelectorAll(SELECTORS.headerLink).forEach(function (el) {
      el.setAttribute('data-wishlist-total', String(total));
    });
  }

  /* ------------------------------------------------------------------
     Public operations
     ------------------------------------------------------------------ */

  function has(productId) {
    load();
    return !!index[String(productId)];
  }

  function count() {
    load();
    return items.length;
  }

  function getItems() {
    load();
    // Newest first — the order the wishlist page renders in.
    return items
      .slice()
      .sort(function (a, b) { return b.addedAt - a.addedAt; })
      .map(function (entry) { return Object.assign({}, entry); });
  }

  /**
   * @param {string|null} productId
   * @param {string} action
   * @param {object} [entry] The affected entry. Passed explicitly because on
   *   `remove` it is already gone from `items`, and analytics listeners still
   *   need its title and price to build a complete payload.
   */
  function emit(productId, action, entry) {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: {
          productId: productId,
          action: action,
          item: entry ? Object.assign({}, entry) : null,
          count: count(),
          items: getItems()
        }
      })
    );
  }

  function add(raw) {
    load();
    var entry = normalize(raw);
    if (!entry) return false;
    if (index[entry.id]) return false; // Never duplicated locally.

    items.push(entry);
    if (items.length > MAX_ITEMS) {
      var dropped = items
        .slice()
        .sort(function (a, b) { return a.addedAt - b.addedAt; })
        .slice(0, items.length - MAX_ITEMS);
      dropped.forEach(function (old) {
        items.splice(items.indexOf(old), 1);
      });
    }
    rebuildIndex();
    writeStorage();

    buttonsFor(entry.id).forEach(paintButton);
    paintHeaderCount();
    toast(STRINGS.added);
    emit(entry.id, 'add', entry);
    sendEvent('add', entry);
    return true;
  }

  /**
   * @param {string|number} productId
   * @param {{silent?: boolean}} [options] `silent` suppresses the toast and the
   *   analytics event. Used when the wishlist page prunes an entry whose
   *   product no longer exists in Shopify — that is housekeeping, not a
   *   customer action.
   */
  function remove(productId, options) {
    load();
    var opts = options || {};
    var id = String(productId);
    var entry = index[id];
    if (!entry) return false;

    items.splice(items.indexOf(entry), 1);
    rebuildIndex();
    writeStorage();

    buttonsFor(id).forEach(paintButton);
    paintHeaderCount();
    if (!opts.silent) toast(STRINGS.removed);
    emit(id, opts.silent ? 'prune' : 'remove', entry);
    if (!opts.silent) sendEvent('remove', entry);
    return true;
  }

  function toggle(raw) {
    var entry = normalize(raw);
    if (!entry) return false;
    return has(entry.id) ? remove(entry.id) : add(entry);
  }

  /**
   * Drop the in-memory mirror so the next read comes from localStorage again.
   * Used by the cross-tab storage listener, and available to integrators who
   * write the storage key from outside this module.
   */
  function reload() {
    items = null;
    index = null;
    load();
  }

  function clear() {
    load();
    var ids = items.map(function (entry) { return entry.id; });
    items = [];
    rebuildIndex();
    writeStorage();
    ids.forEach(function (id) { buttonsFor(id).forEach(paintButton); });
    paintHeaderCount();
    emit(null, 'clear');
  }

  /* ------------------------------------------------------------------
     Markup helper — used by the wishlist page and by the recently-viewed
     patch below so heart markup exists in exactly one shape.
     ------------------------------------------------------------------ */

  function escapeAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function buttonHTML(product, context) {
    if (!product || product.id == null) return '';
    var variantId =
      product.variantId != null
        ? product.variantId
        : product.variants && product.variants.length
          ? product.variants[0].id
          : '';
    // Mirrors the attributes snippets/wishlist-button.liquid emits, so a card
    // built in JS produces the same GA4 payload as one rendered by Liquid.
    var price = normalizePrice(
      product.price != null
        ? product.price
        : product.variants && product.variants.length
          ? product.variants[0].price
          : null
    );
    return (
      '<button type="button" class="wishlist-button wishlist-button--' +
      escapeAttr(context || 'card') +
      '" data-wishlist-button data-product-id="' + escapeAttr(product.id) +
      '" data-product-handle="' + escapeAttr(product.handle || '') +
      '" data-product-title="' + escapeAttr(product.title || '') +
      '"' + (price !== null ? ' data-product-price="' + price + '"' : '') +
      (product.vendor ? ' data-product-vendor="' + escapeAttr(product.vendor) + '"' : '') +
      (product.type ? ' data-product-type="' + escapeAttr(product.type) + '"' : '') +
      (variantId ? ' data-variant-id="' + escapeAttr(variantId) + '"' : '') +
      ' aria-pressed="false" aria-label="' + escapeAttr(STRINGS.add) + '">' +
      '<span class="wishlist-button__icon" aria-hidden="true">' +
      '<svg class="wishlist-button__heart" viewBox="0 0 24 24" focusable="false" aria-hidden="true" role="presentation">' +
      '<path d="M12 20.4 3.9 12.6a5 5 0 0 1 0-7.2 5.2 5.2 0 0 1 7.3 0l.8.8.8-.8a5.2 5.2 0 0 1 7.3 0 5 5 0 0 1 0 7.2Z"/>' +
      '</svg></span>' +
      '<span class="wishlist-button__count" data-wishlist-love-count hidden></span>' +
      '<span class="wishlist-button__tooltip" aria-hidden="true" data-wishlist-tooltip>' +
      escapeAttr(STRINGS.add) + '</span></button>'
    );
  }

  /* ------------------------------------------------------------------
     Delegated interaction — one listener, for the whole document.
     A real <button type="button"> is used everywhere, so Enter and Space
     already produce click events; no extra key handling is required.
     ------------------------------------------------------------------ */

  document.addEventListener(
    'click',
    function (evt) {
      var btn = evt.target && evt.target.closest ? evt.target.closest(SELECTORS.button) : null;
      if (!btn) return;

      // Capture phase, so this runs before any handler the theme binds to the
      // card itself. preventDefault cancels the wrapping <a> that several card
      // snippets use; stopPropagation guarantees a heart click is never also
      // read as a card click by quick view, the Trends analytics delegate, or
      // anything else listening further down.
      evt.preventDefault();
      evt.stopPropagation();

      if (btn.disabled) return;

      var entry = {
        id: btn.getAttribute('data-product-id'),
        handle: btn.getAttribute('data-product-handle'),
        title: btn.getAttribute('data-product-title'),
        variantId: btn.getAttribute('data-variant-id'),
        price: btn.getAttribute('data-product-price'),
        vendor: btn.getAttribute('data-product-vendor'),
        type: btn.getAttribute('data-product-type')
      };

      btn.classList.remove(CLASSES.pop);
      // Reading offsetWidth restarts the CSS animation on a repeat click.
      void btn.offsetWidth;
      btn.classList.add(CLASSES.pop);

      toggle(entry);
    },
    true
  );

  /* ------------------------------------------------------------------
     Re-hydration triggers
     ------------------------------------------------------------------ */

  var pendingRefresh = false;

  function scheduleRefresh() {
    if (pendingRefresh) return;
    pendingRefresh = true;
    raf(function () {
      pendingRefresh = false;
      refresh(document);
    });
  }

  // Theme + Shopify events. Bound once, at module scope, so a section that
  // reloads twenty times never accumulates twenty listeners.
  [
    'shopify:section:load',
    'shopify:section:reorder',
    'shopify:block:select',
    'collection:reloaded',
    'recommendations:loaded',
    'quickview:loaded',
    'quickview:open',
    'predictive-search:open',
    'cart:updated',
    'wishlist:page-rendered'
  ].forEach(function (name) {
    document.addEventListener(name, scheduleRefresh);
  });

  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('popstate', scheduleRefresh);

  // Cross-tab sync: another tab changed the wishlist, mirror it here.
  window.addEventListener('storage', function (evt) {
    if (evt.key !== STORAGE_KEY) return;
    reload();
    scheduleRefresh();
  });

  // Catch-all for markup injected by code that fires no event of its own
  // (predictive search results, third-party apps, custom AJAX). Cheap: it
  // only schedules a rAF when an added subtree actually contains a button.
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches(SELECTORS.button)) ||
            (node.querySelector && node.querySelector(SELECTORS.button))
          ) {
            scheduleRefresh();
            return;
          }
        }
      }
    });

    var startObserving = function () {
      if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) {
      startObserving();
    } else {
      document.addEventListener('DOMContentLoaded', startObserving);
    }
  }

  /* ------------------------------------------------------------------
     Recently viewed / predictive-search cards are built in JS by the theme
     and carry no product id. Wrapping the theme's builder — rather than
     editing theme.js — adds the id and the heart while leaving the original
     markup byte-for-byte intact.
     ------------------------------------------------------------------ */

  function patchGridItemBuilder() {
    if (!window.theme || typeof window.theme.buildProductGridItem !== 'function') return;
    if (window.theme.buildProductGridItem.__tfWishlistPatched) return;

    var original = window.theme.buildProductGridItem;

    var patched = function (products, gridWidth, rowOf, imageSizes) {
      var html = original.apply(this, arguments);
      if (!html || !products || !products.length) return html;

      try {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var cards = wrapper.querySelectorAll('.grid-product');
        // The builder emits one card per product, in order.
        for (var i = 0; i < cards.length && i < products.length; i++) {
          var product = products[i];
          if (!product || product.id == null) continue;
          cards[i].setAttribute('data-product-id', product.id);
          if (product.handle) cards[i].setAttribute('data-product-handle', product.handle);
          var content = cards[i].querySelector('.grid-product__content') || cards[i];
          content.insertAdjacentHTML('afterbegin', buttonHTML(product, 'card'));
        }
        return wrapper.innerHTML;
      } catch (e) {
        // Never let a decoration break the theme's own rendering.
        return html;
      }
    };

    patched.__tfWishlistPatched = true;
    window.theme.buildProductGridItem = patched;
  }

  patchGridItemBuilder();
  document.addEventListener('DOMContentLoaded', patchGridItemBuilder);

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function boot() {
    load();
    refresh(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ------------------------------------------------------------------
     Public API
     ------------------------------------------------------------------ */

  window.TrufairsWishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    reload: reload,
    has: has,
    count: count,
    getItems: getItems,
    refresh: refresh,
    // Exposed so the wishlist page can surface a cart error in the same
    // toast region instead of standing up a second notification system.
    toast: toast,
    buttonHTML: buttonHTML,
    getVisitorId: getVisitorId,
    apiEnabled: apiEnabled,
    config: CFG,
    STORAGE_KEY: STORAGE_KEY
  };
})();
